/* Feb-2026 — Site Mode toggle.
 *
 * Flips the pre-launch "This Site Is In Test Mode Only" banner on/off
 * site-wide. Backed by `app_config.landing_home.site_mode.banner_enabled`
 * and consumed by `<PreLaunchBanner />` on every route.
 *
 * Lives inside the KeysPanel (Admin → Keys → Site Mode) because it's a
 * launch-day toggle, same audience as the payment / email switches.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Rocket, PowerOff, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function SiteModeCard() {
  const [state, setState] = useState(null);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/admin/landing`, { withCredentials: true });
      const sm = r.data?.site_mode || {};
      setState(sm);
      setTitle(sm.banner_title || '');
      setSubtitle(sm.banner_subtitle || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load site mode.');
    }
  };
  useEffect(() => { reload(); }, []);

  const patch = async (site_mode) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/landing`, { site_mode },
        { withCredentials: true });
      const sm = r.data?.site_mode || {};
      setState(sm);
      return sm;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
      return null;
    } finally { setBusy(false); }
  };

  const goLive = async () => {
    const sm = await patch({ banner_enabled: false });
    if (sm) toast.success('Site is LIVE — pre-launch banner hidden site-wide.');
  };
  const returnToTestMode = async () => {
    const sm = await patch({ banner_enabled: true });
    if (sm) toast.info('Site returned to test mode — banner is visible again.');
  };
  const saveText = async () => {
    const sm = await patch({ banner_title: title, banner_subtitle: subtitle });
    if (sm) toast.success('Banner text saved.');
  };

  if (!state) return null;
  const enabled = Boolean(state.banner_enabled);

  return (
    <div
      className={
        'mt-4 rounded-md border p-4 ' +
        (enabled
          ? 'border-warning/40 bg-warning/5'
          : 'border-success/40 bg-success/5')
      }
      data-testid="admin-site-mode"
    >
      <div className="flex items-start gap-2">
        {enabled
          ? <Rocket className="mt-0.5 h-4 w-4 text-warning-foreground" />
          : <Rocket className="mt-0.5 h-4 w-4 text-success" />}
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">
            Site Mode — {enabled ? 'Pre-launch (test mode)' : 'LIVE'}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {enabled
              ? 'The transparent "This Site Is In Test Mode Only" banner is currently painted over every public page. Flip to LIVE the moment the store opens.'
              : 'The pre-launch banner is hidden site-wide. Real customers see the fully-live storefront.'}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {enabled ? (
          <Button
            size="sm"
            onClick={goLive}
            disabled={busy}
            className="h-8 gap-1.5"
            data-testid="admin-site-mode-go-live"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
            Go LIVE — hide banner
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={returnToTestMode}
            disabled={busy}
            className="h-8 gap-1.5"
            data-testid="admin-site-mode-return-test"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
            Return to test mode
          </Button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <div>
          <Label className="text-[11px]">Banner headline</Label>
          <Textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            rows={2}
            className="mt-1 text-[12px]"
            data-testid="admin-site-mode-title"
          />
        </div>
        <div>
          <Label className="text-[11px]">Banner sub-text</Label>
          <Textarea
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            rows={3}
            className="mt-1 text-[12px]"
            data-testid="admin-site-mode-subtitle"
          />
        </div>
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={saveText}
            disabled={busy}
            className="h-8 gap-1.5"
            data-testid="admin-site-mode-save-text"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save banner text
          </Button>
        </div>
      </div>
    </div>
  );
}
